import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import Sidebar from '../layouts/Sidebar';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import CommandCenterService from '../services/CommandCenterService';
import AnnouncementMemberService from '../services/AnnouncementMemberService';
import { buildRoster } from '../utils/utils';

const FLOW_STEPS = [
  { step: '1', title: 'Receive announcement / drill', note: 'Push notification with emergency priority.' },
  { step: '2', title: 'View METHANE card', note: 'Exact location, hazards, access, casualties.' },
  { step: '3', title: 'Accept / reject', note: 'Reason captured if rejected.' },
  { step: '4', title: 'Live GPS / ETA / battery', note: 'Visible in command dashboard.' },
];

export default function StaffApp() {
  const [broadcastTarget, setBroadcastTarget] = useState(null); // null closed, string = target label
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const commandResponse = await CommandCenterService.getStats();
        const activeIncident = commandResponse?.data?.activeIncident || null;

        if (activeIncident) {
          const { entity } = await AnnouncementMemberService.findById(activeIncident.id);
          setRoster(buildRoster(entity || [], activeIncident.lat, activeIncident.lng));
        } else {
          setRoster([]);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    load();
    const intervalId = setInterval(load, 15000);
    return () => clearInterval(intervalId);
  }, []);

  const headCells = [
    { id: 'name', label: 'Staff' },
    { id: 'role', label: 'Designation' },
    { id: 'status', label: 'Mobile Status', render: (row) => <Badge>{row.status}</Badge> },
    { id: 'eta', label: 'ETA' },
    { id: 'lastPing', label: 'Last Ping', render: (row) => `${row.distance} • ${row.battery}` },
    {
      id: 'action',
      label: 'Action',
      render: (row) => (
        <button
          className="text-seha-500 text-xs font-medium hover:underline"
          onClick={() => setBroadcastTarget(row.name)}
        >
          Message
        </button>
      ),
    },
  ];

  return (
    <Sidebar title="Staff Mobile App">
      <section className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-6 dark:bg-ink-900 dark:border-neutral-700">
            <h3 className="font-bold text-neutral-800 mb-4 dark:text-neutral-100">Staff Mobile Notification Flow</h3>
            <div className="space-y-3">
              {FLOW_STEPS.map((s) => (
                <div key={s.step} className="timeline-row">
                  <time>{s.step}</time>
                  <div>
                    <b>{s.title}</b>
                    <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">{s.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="xl:col-span-2 bg-white rounded-xl border border-neutral-200 shadow-subtle overflow-hidden dark:bg-ink-900 dark:border-neutral-700">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between dark:border-neutral-800">
              <h3 className="font-bold text-neutral-800 dark:text-neutral-100">Staff App Response Board</h3>
              <Button size="md" onClick={() => setBroadcastTarget('All active staff')}>
                Broadcast
              </Button>
            </div>
            <DataTable
              headCells={headCells}
              rows={roster}
              loading={loading}
              emptyLabel={loading ? 'Loading staff roster…' : 'No team assigned.'}
            />
          </div>
        </div>
      </section>

      <Modal
        open={Boolean(broadcastTarget)}
        onClose={() => setBroadcastTarget(null)}
        title="Broadcast Message"
        subtitle="Push, SMS, email, voice and staff mobile app notification."
        confirmLabel="Send Broadcast"
        onConfirm={() => {
          toast.success(`Broadcast sent to ${broadcastTarget}.`);
          setBroadcastTarget(null);
        }}
      >
        <div className="space-y-5">
          <div>
            <label className="form-label" htmlFor="staff-broadcast-target">Target</label>
            <input id="staff-broadcast-target" className="form-input" value={broadcastTarget || ''} readOnly />
          </div>
          <div>
            <label className="form-label" htmlFor="staff-broadcast-message">Message</label>
            <textarea
              id="staff-broadcast-message"
              className="form-input"
              rows={4}
              defaultValue="Maintain zone separation. Confirm decon clearance before transfer."
            />
          </div>
        </div>
      </Modal>
    </Sidebar>
  );
}
